import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import TooltipHandler from '@/components/ui/custom/TooltipHandler';
import { MaterialMode as Mode } from '@/data/context/CourseEditingContext';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { createBook, GetBookParams } from '@/data/services/bookServices';
import { createChapter } from '@/data/services/chapterServices';
import { updateLesson } from '@/data/services/lessonServices';
import { booksQueryKeys } from '@/data/services/querykeys';
import { createSection } from '@/data/services/sectionServices';
import { createSubchapter } from '@/data/services/subchapterServices';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import { bookType } from '@/models/Book';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Lesson from '@/models/Lesson';
import LoadingView from '@/pages/courses/LoadingView';
import { buildMaterialUrl } from '@/utils/book/bookUrl';
import alert from '@/utils/shared/alert';
import { ArrowUpTrayIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AddMaterial from './AddMaterial';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

interface MaterialModeProps {
  course?: CoursePath;
  lesson: Lesson;
  updateLessons?: (lessonId?: number) => Promise<void>;
  bookType: bookType;
  isLoadingContainer: boolean;
  blockId?: number;
  updateBook?: (bookId: number) => void;
}

export default function MaterialMode({
  course,
  lesson,
  updateLessons,
  bookType,
  isLoadingContainer,
  blockId,
  updateBook,
}: MaterialModeProps) {
  const { materialMode, setMaterialMode, allowBigChanges } = useCourseEditing();
  const { t } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });
  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isLimitedEdition } = usePedWorkspace();

  const cantEdit = !allowBigChanges || isLimitedEdition;

  const getBookRoute = (lessonId: number, bookId: number) =>
    buildMaterialUrl({
      lessonId,
      bookType,
      bookId,
      slugCourseName: course?.slug,
      blockId,
    });

  const { mutate: sectionCreate, isPending: isLoadingSection } = useMutation(
    createSection,
    {
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
      onSuccess: (_, variables) => {
        updateLessons?.();
        updateBook?.(variables.bookId);
      },
    },
  );

  const { mutate: subchapterCreate, isPending: loadingSubchapter } =
    useMutation(createSubchapter, {
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
      onSuccess: async (subchapter, variables) => {
        const { id: subchapterId } = subchapter;
        const { lessonId, bookId, chapterId } = variables;

        sectionCreate({
          bookId,
          chapterId,
          lessonId,
          subchapterId,
        });
      },
    });

  const handleCreateChapter = (params: GetBookParams) => {
    if (materialMode === 'slides')
      return createChapter(params, { content: '' });
    else return createChapter(params);
  };

  const { mutate: chapterCreate, isPending: isLoadingChapter } = useMutation(
    handleCreateChapter,
    {
      onSuccess(chapter, variables) {
        if (materialMode === 'default')
          navigate(getBookRoute(variables.lessonId, chapter.book));
        else {
          const { id: chapterId } = chapter;
          const { lessonId, bookId, bookType } = variables;

          const params = {
            lessonId,
            bookId,
            chapterId,
            bookType,
          };

          subchapterCreate(params);
        }
      },
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
    },
  );

  const { mutate: bookCreate, isPending: isLoadingBook } = useMutation(
    createBook,
    {
      onSuccess: async (data, variables) => {
        chapterCreate({
          lessonId: variables.lessonId,
          bookType: data.bookType,
          bookId: data.id,
        });

        await queryClient.invalidateQueries({
          queryKey: booksQueryKeys.get(data.id).queryKey,
        });

        await updateLessons?.(variables.lessonId);
      },
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
    },
  );
  const editLesson = async (lesson: Lesson) => {
    if (isPulished(lesson.status) && course) {
      const updatedLesson = await updateLesson(
        {
          slugCourseName: course.slug,
          lessonId: lesson.id,
          blockId,
        },
        {},
      );
      return updatedLesson;
    }
    return lesson;
  };
  const onClickCreate = async (mode: Exclude<Mode, 'upload'>) => {
    setMaterialMode(mode);
    const updatedLesson = await editLesson(lesson);
    bookCreate({
      bookType,
      lessonId: updatedLesson.id,
    });
  };

  const isLoadingCreate =
    isLoadingBook || isLoadingChapter || loadingSubchapter || isLoadingSection;

  if (isLoadingContainer) {
    return <LoadingView />;
  }

  switch (materialMode) {
    case 'upload':
      return (
        <AddMaterial
          course={course}
          lesson={lesson}
          updateLessons={updateLessons}
          bookType={bookType}
          setMaterialMode={setMaterialMode}
          blockId={blockId}
        />
      );
    default:
      return (
        <div className="flex gap-4">
          <TooltipHandler
            tooltipMessage={editRulesT('cantModify')}
            renderTooltip={cantEdit}
          >
            <MainButton
              text={t('uploadMaterial')}
              icon={<ArrowUpTrayIcon />}
              disabled={cantEdit}
              onClick={() => setMaterialMode('upload')}
            />
          </TooltipHandler>
          <TooltipHandler
            tooltipMessage={editRulesT('cantModify')}
            renderTooltip={cantEdit}
          >
            <MainButton
              text={t('createMaterial')}
              icon={<PencilIcon />}
              loading={isLoadingCreate && materialMode === 'default'}
              disabled={cantEdit}
              onClick={() => onClickCreate('default')}
            />
          </TooltipHandler>
          <ConditionalRenderer condition={bookType === 'STUDENT'}>
            <TooltipHandler
              tooltipMessage={editRulesT('cantModify')}
              renderTooltip={cantEdit}
            >
              <MainButton
                text={t('createSlidesMaterial')}
                icon={<PencilIcon />}
                disabled={cantEdit}
                loading={isLoadingCreate && materialMode === 'slides'}
                onClick={() => onClickCreate('slides')}
              />
            </TooltipHandler>
          </ConditionalRenderer>
        </div>
      );
  }
}
